import React from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';

type EmphasisedValueTone = 'discount' | 'balance' | 'tag';

type EmphasisedValueProps = Readonly<{
  /**
   * `discount` is money the couple SAVES. It takes the green every cash-fund surface already reads
   * as a saving (`FundCouponChip`), so a discount is never mistaken for the couple's own money.
   *
   * `balance` is money the couple ALREADY HAS. Bold only, inheriting its sentence's colour:
   * colouring it green would claim a saving that has not happened — the fund is not a discount.
   *
   * `tag` is the same saving as a small pill, for a rate that reads better as a label than as a
   * number in parentheses. Matches `FundCouponChip`'s chip variant.
   */
  tone: EmphasisedValueTone;
  children: React.ReactNode;
}>;

/**
 * One value lifted out of a sentence, for the cash-fund and honeymoon copy.
 *
 * Inherits its font size and line height rather than setting them: `TextV2` otherwise falls back to
 * its own default, which rendered the balance SMALLER than the headline it sat inside.
 *
 * Use it only where the surrounding text is not already emphasised. A number inside an all-bold
 * headline needs nothing — bolding it again changes nothing and only splits the string.
 *
 * Pair with `withPhraseValues`, so the sentence around it stays one translatable unit.
 */
export const EmphasisedValue: React.FC<EmphasisedValueProps> = ({ tone, children }) => {
  if (tone === 'tag') {
    return (
      <Box
        as="span"
        display="inline-block"
        paddingX={pxToRem(8)}
        paddingY={pxToRem(1)}
        borderRadius={pxToRem(4)}
        backgroundColor="positive2"
        color="positive7"
        fontWeight={700}
        fontSize="0.875em"
        whiteSpace="nowrap"
        verticalAlign="baseline"
      >
        {children}
      </Box>
    );
  }

  return (
    <TextV2
      as="span"
      fontSize="inherit"
      lineHeight="inherit"
      fontWeight={700}
      color={tone === 'discount' ? 'positive7' : 'inherit'}
    >
      {children}
    </TextV2>
  );
};

EmphasisedValue.displayName = 'EmphasisedValue';
